import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import ActivityTab from './ActivityTab';
import {
  Award, Target, Plus, X, ChevronRight, ChevronDown, Users, ShoppingBag, ThumbsUp,
  FileText, Video, Music, Image as ImageIcon, Activity, Clock, Calendar
} from 'lucide-react';

const SessionsSection = ({ sessionTypes, userId, showAsOwner }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = React.useState(false);
  const activeSessions = sessionTypes.filter(s => s.is_active !== false);
  const visibleSessions = expanded ? activeSessions : activeSessions.slice(0, 2);

  if (activeSessions.length === 0 && !showAsOwner) return null;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6" data-testid="sessions-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
          <Video className="h-5 w-5 mr-2 text-green-500" />Sessions disponibles
        </h2>
        {showAsOwner && (
          <button onClick={() => navigate('/app/sessions')} className="text-xs text-purple-600 dark:text-purple-400 hover:text-purple-700 font-medium" data-testid="sidebar-manage-sessions-btn">
            Gérer
          </button>
        )}
      </div>

      {activeSessions.length === 0 ? (
        <div className="text-center py-4">
          <Video className="h-8 w-8 mx-auto text-gray-300 dark:text-gray-600 mb-2" />
          <p className="text-sm text-gray-400 dark:text-gray-500">Aucune session proposée</p>
          {showAsOwner && (
            <button onClick={() => navigate('/app/sessions')} className="mt-2 text-xs font-medium text-purple-600 dark:text-purple-400 hover:text-purple-700" data-testid="sidebar-create-session-empty-btn">
              + Créer une session
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {visibleSessions.map((st) => (
              <div key={st.session_type_id} className="group p-3 rounded-xl border border-gray-100 dark:border-gray-700 hover:border-purple-200 dark:hover:border-purple-800 transition-all hover:shadow-sm" data-testid={`session-card-${st.session_type_id}`}>
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <h4 className="font-semibold text-gray-900 dark:text-white text-sm">{st.title}</h4>
                    {st.description && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">{st.description}</p>}
                  </div>
                  <span className={`ml-2 flex-shrink-0 text-xs font-bold px-2 py-1 rounded-full ${
                    st.is_free ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                  }`}>
                    {st.is_free ? 'Gratuit' : `${st.price} ${st.currency || 'CHF'}`}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-gray-400 dark:text-gray-500 flex items-center">
                    <Clock className="h-3 w-3 mr-1" />{st.duration} min
                  </span>
                  {!showAsOwner && (
                    <button
                      onClick={() => navigate(`/app/profile/${userId}?book=${st.session_type_id}`)}
                      className="text-xs font-medium text-white bg-purple-600 hover:bg-purple-700 px-3 py-1 rounded-lg transition-colors"
                      data-testid={`book-session-${st.session_type_id}`}
                    >
                      Réserver
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          {activeSessions.length > 2 && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="w-full mt-3 py-2 text-xs font-medium text-purple-600 dark:text-purple-400 hover:text-purple-700 flex items-center justify-center gap-1"
              data-testid="sessions-expand-btn"
            >
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`} />
              {expanded ? 'Voir moins' : `Voir ${activeSessions.length - 2} session${activeSessions.length - 2 > 1 ? 's' : ''} de plus`}
            </button>
          )}
        </>
      )}
    </div>
  );
};

const ProfileSidebar = ({
  profile, userId, showAsOwner, isEditing, editData, setEditData,
  products, communities, badges, sessionTypes,
  onAddSkill, onRemoveSkill
}) => {
  const navigate = useNavigate();

  return (
    <div className="space-y-6">
      {/* Skills — Brief Jun 14 (Vague 2 §1) : pills flat, sans gradient ni barre gauche.
          Utilise les design tokens (light + dark via CSS variables). */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <Award className="h-5 w-5 mr-2 text-yellow-500" />Compétences
          </h2>
          {isEditing && (<Button size="sm" variant="ghost" onClick={onAddSkill}><Plus className="h-4 w-4" /></Button>)}
        </div>
        {(isEditing ? editData.skills : profile.skills)?.length > 0 ? (
          <div className="flex flex-wrap gap-2" data-testid="sidebar-skills-pills">
            {(isEditing ? editData.skills : profile.skills).map((skill, index) => (
              <span
                key={index}
                className={`px-3 py-1 rounded-md text-[12px] font-medium ${isEditing ? 'flex items-center gap-1' : ''}`}
                style={{
                  background: 'var(--color-background-secondary)',
                  border: '0.5px solid var(--color-border-tertiary)',
                  borderRadius: 'var(--border-radius-md)',
                  color: 'var(--color-text-secondary)',
                }}
                data-testid={`sidebar-skill-pill-${index}`}
              >
                {skill}
                {isEditing && (<button onClick={() => onRemoveSkill(index)} className="ml-1 text-red-400 hover:text-red-600"><X className="h-3 w-3" /></button>)}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-gray-400 dark:text-gray-500 text-center py-2 text-sm">Aucune compétence</p>
        )}
      </div>

      {/* Sessions disponibles */}
      <SessionsSection sessionTypes={sessionTypes || []} userId={userId} showAsOwner={showAsOwner} />

      {/* Products */}
      {products.length > 0 && (
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <ShoppingBag className="h-5 w-5 mr-2 text-purple-500" />Produits populaires
            </h2>
            <button onClick={() => navigate(`/app/shop/${userId}`)} className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center">
              Voir tout<ChevronRight className="h-4 w-4 ml-1" />
            </button>
          </div>
          <div className="space-y-3">
            {products.map((product) => {
              const icons = { pdf: FileText, video: Video, audio: Music, image: ImageIcon, document: FileText };
              const Icon = icons[product.content_type] || FileText;
              const colors = { pdf: 'text-red-500 bg-red-50', video: 'text-purple-500 bg-purple-50', audio: 'text-green-500 bg-green-50', image: 'text-pink-500 bg-pink-50', document: 'text-blue-500 bg-blue-50' };
              const colorClass = colors[product.content_type] || 'text-gray-500 bg-gray-50';
              return (
                <div key={product.product_id} onClick={() => navigate(`/app/shop/${userId}`)} className="flex items-center gap-3 p-3 rounded-xl hover:bg-purple-50 cursor-pointer transition-all border border-gray-100 hover:border-purple-200 group">
                  {product.thumbnail_url ? (
                    <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0"><img src={product.thumbnail_url} alt={product.title} className="w-full h-full object-cover" /></div>
                  ) : (
                    <div className={`w-16 h-16 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClass.split(' ')[1]}`}><Icon className={`h-8 w-8 ${colorClass.split(' ')[0]}`} /></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate group-hover:text-purple-600 transition-colors">{product.title}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${colorClass}`}>{product.content_type?.toUpperCase()}</span>
                      {product.like_count > 0 && (<span className="text-xs text-gray-500 flex items-center"><ThumbsUp className="h-3 w-3 mr-1" />{product.like_count}</span>)}
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    {product.is_free ? (
                      <span className="text-xs font-semibold text-green-600 bg-green-100 px-2 py-1 rounded-full">Gratuit</span>
                    ) : (
                      <span className="text-xs font-semibold text-purple-600 bg-purple-100 px-2 py-1 rounded-full">{product.price} {product.currency}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <button onClick={() => navigate(`/app/shop/${userId}`)} className="w-full mt-4 py-2.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm font-medium rounded-xl hover:opacity-90 transition-opacity flex items-center justify-center">
            <ShoppingBag className="h-4 w-4 mr-2" />Voir toute la boutique
          </button>
        </div>
      )}

      {/* Communities */}
      {communities.length > 0 && (
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center mb-4">
            <Users className="h-5 w-5 mr-2 text-purple-500" />Communautés
          </h2>
          <div className="space-y-3">
            {communities.map((community) => (
              <div key={community.community_id} onClick={() => navigate(`/app/community/${community.community_id}`)} className="flex items-center gap-3 p-2 rounded-lg hover:bg-purple-50 cursor-pointer transition-colors">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={community.avatar_url} />
                  <AvatarFallback className="bg-gradient-to-br from-purple-500 to-pink-500 text-white text-sm font-bold">{community.name?.substring(0, 2).toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{community.name}</p>
                  <p className="text-xs text-gray-500">{community.member_count} membres</p>
                </div>
                <ChevronRight className="h-4 w-4 text-gray-400" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Activity Feed */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center mb-4">
          <Activity className="h-5 w-5 mr-2 text-orange-500" />Activité récente
        </h2>
        <ActivityTab userId={userId} />
      </div>
    </div>
  );
};

export default ProfileSidebar;